import { Effect } from 'postprocessing';
import { Uniform } from 'three';
import { extend, useFrame } from '@react-three/fiber';
import { useRef } from 'react';

class LensDistortionEffectImpl extends Effect {
    constructor() {
        super(
            'LensDistortionEffect',
            `
                uniform float uTime;
                uniform vec2 uResolution;
                uniform float uK;
                uniform float uKCube;
                uniform float uScale;
                uniform float uDispersion;
                uniform bool uBlurEnabled;
                uniform float uBlurAmount;
                uniform bool uDebug;
                uniform sampler2D tDiffuse;

                vec2 rand(vec2 co) {
                    float noise1 = fract(sin(dot(co, vec2(12.9898, 78.233))) * 43758.5453);
                    float noise2 = fract(sin(dot(co, vec2(12.9898, 78.233) * 2.0)) * 43758.5453);
                    return clamp(vec2(noise1, noise2), 0.0, 1.0);
                }

                vec3 blur(vec2 uv, vec2 coords) {
                    vec2 noise = rand(uv);
                    float tolerance = 0.2;
                    float vignette_size = 0.5;
                    vec2 powers = pow(abs(vec2(uv.x - 0.5, uv.y - 0.5)), vec2(2.0));
                    float radiusSqrd = pow(vignette_size, 2.0);
                    float gradient = smoothstep(radiusSqrd - tolerance, radiusSqrd + tolerance, powers.x + powers.y);

                    vec4 col = vec4(0.0);

                    float X1 = coords.x + uBlurAmount * noise.x * 0.004 * gradient;
                    float Y1 = coords.y + uBlurAmount * noise.y * 0.004 * gradient;
                    float X2 = coords.x - uBlurAmount * noise.x * 0.004 * gradient;
                    float Y2 = coords.y - uBlurAmount * noise.y * 0.004 * gradient;

                    float invX1 = coords.x + uBlurAmount * ((1.0 - noise.x) * 0.004) * (gradient * 0.5);
                    float invY1 = coords.y + uBlurAmount * ((1.0 - noise.y) * 0.004) * (gradient * 0.5);
                    float invX2 = coords.x - uBlurAmount * ((1.0 - noise.x) * 0.004) * (gradient * 0.5);
                    float invY2 = coords.y - uBlurAmount * ((1.0 - noise.y) * 0.004) * (gradient * 0.5);

                    col += texture(tDiffuse, vec2(X1, Y1)) * 0.1;
                    col += texture(tDiffuse, vec2(X2, Y2)) * 0.1;
                    col += texture(tDiffuse, vec2(X1, Y2)) * 0.1;
                    col += texture(tDiffuse, vec2(X2, Y1)) * 0.1;

                    col += texture(tDiffuse, vec2(invX1, invY1)) * 0.15;
                    col += texture(tDiffuse, vec2(invX2, invY2)) * 0.15;
                    col += texture(tDiffuse, vec2(invX1, invY2)) * 0.15;
                    col += texture(tDiffuse, vec2(invX2, invY1)) * 0.15;

                    return col.rgb;
                }

                void mainImage(const in vec4 inputColor, const in vec2 uv, out vec4 outputColor) {
                    if (uDebug) {
                        outputColor = texture(tDiffuse, uv);
                        return;
                    }

                    float k = uK;
                    float dispersion = uDispersion;

                    vec3 eta = vec3(1.0 + dispersion * 0.9, 1.0 + dispersion * 0.6, 1.0 + dispersion * 0.3);
                    vec2 texcoord = uv;

                    float aspect = uResolution.x / uResolution.y;
                    vec2 cancoord = uv;
                    cancoord.x = (uv.x - 0.5) * aspect + 0.5;

                    float r2 = (cancoord.x - 0.5) * (cancoord.x - 0.5) + (cancoord.y - 0.5) * (cancoord.y - 0.5);
                    float f = uKCube == 0.0 ? 1.0 + r2 * k : 1.0 + r2 * (k + uKCube * sqrt(r2));

                    vec2 rCoords = (f * eta.r) * uScale * (texcoord - vec2(0.5)) + vec2(0.5);
                    vec2 gCoords = (f * eta.g) * uScale * (texcoord - vec2(0.5)) + vec2(0.5);
                    vec2 bCoords = (f * eta.b) * uScale * (texcoord - vec2(0.5)) + vec2(0.5);

                    vec3 inputDistort = vec3(0.0);

                    if (uBlurEnabled) {
                        inputDistort.r = blur(uv, rCoords).r;
                        inputDistort.g = blur(uv, gCoords).g;
                        inputDistort.b = blur(uv, bCoords).b;
                    } else {
                        inputDistort.r = texture(tDiffuse, rCoords).r;
                        inputDistort.g = texture(tDiffuse, gCoords).g;
                        inputDistort.b = texture(tDiffuse, bCoords).b;
                    }

                    outputColor = vec4(inputDistort, 1.0);
                }
            `
            ,
            {
                uniforms: new Map([
                    ['uTime', new Uniform(0)],
                    ['uResolution', new Uniform({ x: 1, y: 1 })],
                    ['uK', new Uniform(-0.6)],
                    ['uKCube', new Uniform(1.0)],
                    ['uScale', new Uniform(1.0)],
                    ['uDispersion', new Uniform(0.02)],
                    ['uBlurEnabled', new Uniform(true)],
                    ['uBlurAmount', new Uniform(1.7)],
                    ['uDebug', new Uniform(false)]
                ])
            }
        );
    }
}

// Extend LensDistortionEffect into the THREE namespace
extend({ LensDistortionEffect: LensDistortionEffectImpl });

export default function LensDistortionEffect({
    k = -0.6,
    kCube = 1.0,
    scale = 1.0,
    dispersion = 0.02,
    blurEnabled = true,
    blurAmount = 1.7,
    debug = false
}) {
    const effectRef = useRef();

    useFrame((state) => {
        if (effectRef.current) {
            effectRef.current.uniforms.get('uTime').value = state.clock.getElapsedTime();
            effectRef.current.uniforms.get('uResolution').value = {
                x: state.size.width,
                y: state.size.height
            };
            effectRef.current.uniforms.get('uK').value = k;
            effectRef.current.uniforms.get('uKCube').value = kCube;
            effectRef.current.uniforms.get('uScale').value = scale;
            effectRef.current.uniforms.get('uDispersion').value = dispersion;
            effectRef.current.uniforms.get('uBlurEnabled').value = blurEnabled;
            effectRef.current.uniforms.get('uBlurAmount').value = blurAmount;
            effectRef.current.uniforms.get('uDebug').value = debug;
        }
    });

    return <lensDistortionEffect ref={effectRef} />;
}
